/**
 * MaintenanceLoggingModal.js
 *
 * Modal for Technicians to quickly log completed maintenance work
 * Features: Pre-filled device info, maintenance type selection, work description,
 * completion date, and automatic next due date calculation
 *
 * Props:
 * - isOpen: Boolean to show/hide modal
 * - device: Device object with maintenance info
 * - maintenanceType: Type of maintenance (PPM, Calibration, Compliance)
 * - onClose: Callback when modal closes
 * - onSubmit: Callback when form is submitted with maintenance data
 * - token: JWT authentication token
 */

import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { DeviceIDBadge } from '../common/Badges';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import BarcodeScanner from '../../../features/devices/components/BarcodeScanner';
import DocumentManager from '../common/DocumentManager';
import ConfirmationModal from './ConfirmationModal';
import ModalCloseButton from './ModalCloseButton';
import {
  DOCUMENT_OPTIONS_FOR_TECHNICIAN,
  getDocumentOptionsForMaintenanceType,
} from '../common/DocumentTypes';
import './MaintenanceLoggingModal.css';

function DeviceVerifiedBanner({ scannedDeviceId }) {
  return (
    <div className="device-verified-banner" role="status" aria-live="polite">
      <span className="device-verified-icon" aria-hidden="true">✓</span>
      <div className="device-verified-body">
        <span className="device-verified-title">Device verified</span>
        <DeviceIDBadge deviceId={scannedDeviceId || 'N/A'} size="small" />
      </div>
    </div>
  );
}

const MaintenanceLoggingModal = ({ isOpen, device, maintenanceType, onClose, onSubmit, token, user }) => {
  const maintenanceDocumentOptions = useMemo(
    () => getDocumentOptionsForMaintenanceType(maintenanceType),
    [maintenanceType]
  );
  const technicianListDocumentTypes = useMemo(
    () => DOCUMENT_OPTIONS_FOR_TECHNICIAN.map((o) => o.value),
    []
  );

  const maintenanceReportHelperLine =
    maintenanceType === 'PPM'
      ? 'For PPM work, uploads must be Maintenance Reports.'
      : maintenanceType === 'Calibration'
        ? 'For calibration work, uploads must be Calibration Reports.'
        : maintenanceType === 'Compliance'
          ? 'For compliance work, uploads must be Compliance Reports.'
          : 'Upload the report type that matches this maintenance activity.';

  const [loading, setLoading] = useState(false);
  const [isCheckingAssignment, setIsCheckingAssignment] = useState(false);
  const [userAssigned, setUserAssigned] = useState(null);
  const [assignmentError, setAssignmentError] = useState(null);
  const [deviceScanned, setDeviceScanned] = useState(false);
  const [scannedDeviceId, setScannedDeviceId] = useState(null);
  const [scanError, setScanError] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  /** Documents uploaded after this session's first document list load (required before submit). */
  const [pastBaselineDocumentCount, setPastBaselineDocumentCount] = useState(0);
  const [historicalDocumentCount, setHistoricalDocumentCount] = useState(0);
  const [historyDocsExpanded, setHistoryDocsExpanded] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    completionDate: new Date().toISOString().split('T')[0],
    nextDueDate: '',
    hoursSpent: 0
  });
  const [dateRestrictions, setDateRestrictions] = useState({
    min: '',
    max: '',
    calculated: ''
  });

  // Check if user is assigned as technician for this device
  useEffect(() => {
    const checkUserAssignment = async () => {
      if (!isOpen || !device || !token) return;

      setIsCheckingAssignment(true);
      setAssignmentError(null);
      setUserAssigned(null);

      try {
        // Get current user from sessionStorage (set during login)
        const userStr = sessionStorage.getItem('user');
        const userData = userStr ? JSON.parse(userStr) : null;
        const currentUserId = userData?.id;

        if (!currentUserId) {
          setAssignmentError({
            title: 'Authentication Error',
            message: 'Unable to verify your identity. Please log in again.'
          });
          setIsCheckingAssignment(false);
          return;
        }

        // Get device technician assignments
        const response = await axios.get(
          API_ENDPOINTS.DEVICES.TECHNICIANS(device.id),
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        const assignment = response.data;

        // If no assignment exists for this device
        if (!assignment) {
          setUserAssigned(false);
          setAssignmentError({
            title: 'No Assignment',
            message: 'This device has no technician assigned. Contact your manager to request assignment.'
          });
          return;
        }

        // Check if current user is assigned as primary or secondary technician
        // Compare user.id with primary_technician.id and secondary_technician.id
        const primaryTechId = assignment.primary_technician?.id;
        const secondaryTechId = assignment.secondary_technician?.id;
        const isAssigned = currentUserId === primaryTechId || currentUserId === secondaryTechId;

        setUserAssigned(isAssigned);

        if (!isAssigned) {
          const primaryName = assignment.primary_technician?.full_name || 'Unassigned';
          setAssignmentError({
            title: 'Not Assigned to This Device',
            message: `This device is assigned to ${primaryName}. Contact your manager to request reassignment.`
          });
        }
      } catch (error) {
        console.error('Error checking technician assignment:', error);
        setAssignmentError({
          title: 'Authorization Check Failed',
          message: 'Unable to verify your assignment. Please try again or contact support.'
        });
      } finally {
        setIsCheckingAssignment(false);
      }
    };

    checkUserAssignment();
  }, [isOpen, device, token]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && device) {
      const dateRange = getDefaultNextDueDate();
      const completionDateRestrictions = getCompletionDateRestrictions();
      setFormData({
        description: '',
        completionDate: new Date().toISOString().split('T')[0],
        nextDueDate: dateRange.calculated,
        hoursSpent: 0
      });
      setDateRestrictions({
        ...dateRange,
        completionMin: completionDateRestrictions.min,
        completionMax: completionDateRestrictions.max
      });
      // Reset scan state when modal opens
      setDeviceScanned(false);
      setScannedDeviceId(null);
      setScanError(null);
      setShowScanner(false);
      setPastBaselineDocumentCount(0);
      setHistoricalDocumentCount(0);
      setHistoryDocsExpanded(false);
      setShowCancelConfirm(false);
    }
  }, [isOpen, device]);

  useBodyScrollLock(isOpen && !showCancelConfirm);

  const finalizeClose = () => {
    setShowCancelConfirm(false);
    onClose();
  };

  /** Confirm cancel when this session uploaded reports (they stay on the device). */
  const handleRequestClose = () => {
    if (loading) return;
    if (pastBaselineDocumentCount > 0) {
      setShowCancelConfirm(true);
      return;
    }
    finalizeClose();
  };

  // Calculate completion date restrictions (max 2 days old, no future dates)
  const getCompletionDateRestrictions = () => {
    const today = new Date();
    const twoDaysAgo = new Date(today);
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

    return {
      min: twoDaysAgo.toISOString().split('T')[0],
      max: today.toISOString().split('T')[0]
    };
  };

  // Calculate next due date with allowed range (±10% of frequency)
  const getDefaultNextDueDate = () => {
    if (!device) return { calculated: '', min: '', max: '' };

    let frequency = null;
    let frequencyDays = 0;

    if (maintenanceType === 'PPM' && device.ppm_frequency) {
      frequency = device.ppm_frequency;
    } else if (maintenanceType === 'Calibration' && device.calibration_frequency) {
      frequency = device.calibration_frequency;
    } else if (maintenanceType === 'Compliance' && device.compliance_frequency) {
      frequency = device.compliance_frequency;
    }

    if (!frequency) return { calculated: '', min: '', max: '' };

    // Parse frequency string to get days
    const completionDate = new Date(formData.completionDate);
    let calculatedDate = new Date(completionDate);

    if (frequency.toLowerCase().includes('quarterly') || frequency.toLowerCase().includes('3 months')) {
      frequencyDays = 90;
      calculatedDate.setMonth(calculatedDate.getMonth() + 3);
    } else if (frequency.toLowerCase().includes('bi-annual') || frequency.toLowerCase().includes('half yearly') || frequency.toLowerCase().includes('6 months')) {
      frequencyDays = 180;
      calculatedDate.setMonth(calculatedDate.getMonth() + 6);
    } else if (frequency.toLowerCase().includes('annual') || frequency.toLowerCase().includes('yearly') || frequency.toLowerCase().includes('1 year')) {
      frequencyDays = 365;
      calculatedDate.setFullYear(calculatedDate.getFullYear() + 1);
    } else if (frequency.toLowerCase().includes('month')) {
      const months = parseInt(frequency.match(/\d+/)?.[0] || 1);
      frequencyDays = months * 30;
      calculatedDate.setMonth(calculatedDate.getMonth() + months);
    } else if (frequency.toLowerCase().includes('year')) {
      const years = parseInt(frequency.match(/\d+/)?.[0] || 1);
      frequencyDays = years * 365;
      calculatedDate.setFullYear(calculatedDate.getFullYear() + years);
    } else if (frequency.toLowerCase().includes('week')) {
      const weeks = parseInt(frequency.match(/\d+/)?.[0] || 1);
      frequencyDays = weeks * 7;
      calculatedDate.setDate(calculatedDate.getDate() + weeks * 7);
    }

    // Calculate allowed range: ±1% of frequency
    const tolerance = Math.ceil(frequencyDays * 0.01);
    const minDate = new Date(calculatedDate);
    const maxDate = new Date(calculatedDate);

    minDate.setDate(minDate.getDate() - tolerance);
    maxDate.setDate(maxDate.getDate() + tolerance);

    return {
      calculated: calculatedDate.toISOString().split('T')[0],
      min: minDate.toISOString().split('T')[0],
      max: maxDate.toISOString().split('T')[0]
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };

      // Auto-calculate next due date when completion date changes
      if (name === 'completionDate') {
        const dateRange = getDefaultNextDueDate();
        const completionDateRestrictions = getCompletionDateRestrictions();
        updated.nextDueDate = dateRange.calculated;
        setDateRestrictions({
          ...dateRange,
          completionMin: completionDateRestrictions.min,
          completionMax: completionDateRestrictions.max
        });
      }

      return updated;
    });
  };

  // Handle device scan
  const handleDeviceScan = async (scannedCode) => {
    setScanError(null);

    try {
      let deviceIdentifier = null;

      // Check if scanned code is a full URL (for QR codes with base64 tokens)
      if (scannedCode.includes('/device-scan')) {
        try {
          const url = new URL(scannedCode);
          let tokenParam = url.searchParams.get('token');

          if (tokenParam) {
            // Call QR endpoint which decodes the token server-side
            const qrDeviceResponse = await axios.get(
              API_ENDPOINTS.DEVICES.QR_DEVICE(tokenParam)
            );

            if (qrDeviceResponse.data && qrDeviceResponse.data.device_identifier) {
              deviceIdentifier = qrDeviceResponse.data.device_identifier;
            }
          }
        } catch (urlError) {
          // Close scanner on error and show error message
          setShowScanner(false);
          setScanError({
            type: 'parse_error',
            title: 'Invalid QR Code',
            message: 'Failed to parse the scanned QR code. Please try scanning again.'
          });
          return;
        }
      } else {
        // Direct device ID scan
        deviceIdentifier = scannedCode.trim();
      }

      if (deviceIdentifier) {
        // Verify scanned device matches the target device
        const targetDeviceId = device.device_identifier || device.id;

        if (deviceIdentifier.toLowerCase() !== targetDeviceId.toLowerCase()) {
          // Close scanner on mismatch and show error message
          setShowScanner(false);
          setScanError({
            type: 'device_mismatch',
            title: 'Device Mismatch',
            message: `You scanned: ${deviceIdentifier} | Expected: ${targetDeviceId}. Please scan the correct device.`
          });
          toast.warning(`Device mismatch. Expected ${targetDeviceId}`);
          return;
        }

        // Device matches - mark as scanned and close scanner
        setScannedDeviceId(deviceIdentifier);
        setDeviceScanned(true);
        setShowScanner(false);
      }
    } catch (error) {
      console.error('Error during device scan:', error);
      // Close scanner on error and show error message
      setShowScanner(false);
      setScanError({
        type: 'scan_error',
        title: 'Scan Error',
        message: 'Failed to process the device scan. Please try scanning again.'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.description.trim()) {
      toast.error('Please describe the maintenance work performed');
      return;
    }

    // Validate completion date is within allowed range
    if (formData.completionDate < dateRestrictions.completionMin || formData.completionDate > dateRestrictions.completionMax) {
      const minDate = new Date(dateRestrictions.completionMin).toLocaleDateString();
      const maxDate = new Date(dateRestrictions.completionMax).toLocaleDateString();
      toast.error(`Completion date must be between ${minDate} and ${maxDate}`);
      return;
    }

    if (!formData.nextDueDate) {
      toast.error('Please set the next due date');
      return;
    }

    // Validate next due date is within allowed range
    if (formData.nextDueDate < dateRestrictions.min || formData.nextDueDate > dateRestrictions.max) {
      const minDate = new Date(dateRestrictions.min).toLocaleDateString();
      const maxDate = new Date(dateRestrictions.max).toLocaleDateString();
      toast.error(`Next due date must be between ${minDate} and ${maxDate}`);
      return;
    }

    // Validate hours spent is a valid number >= 0.5
    const hoursSpent = parseFloat(formData.hoursSpent);
    if (isNaN(hoursSpent) || hoursSpent < 0.5) {
      toast.error('Hours spent must be at least 0.5');
      return;
    }

    if (pastBaselineDocumentCount < 1) {
      toast.error('Upload at least one report for this maintenance before logging.');
      return;
    }

    setLoading(true);

    try {
      // Create maintenance log entry
      // Note: performed_by is always the logged-in user (server-side), engineers can mention
      // other technicians involved in the description field if needed
      const maintenanceLog = {
        device_id: device.id,
        maintenance_type: maintenanceType,
        work_description: formData.description,
        completion_date: formData.completionDate,
        next_due_date: formData.nextDueDate,
        notes: `Logged via Quick Maintenance Modal for ${maintenanceType}`
      };

      // Submit to backend
      // performed_by_employee_id is always the logged-in user (no override from frontend)
      const response = await axios.post(
        API_ENDPOINTS.DEVICES.MAINTENANCE(device.id),
        {
          maintenance_type: maintenanceLog.maintenance_type,
          maintenance_date: maintenanceLog.completion_date,
          description: maintenanceLog.work_description,
          next_due_date: maintenanceLog.next_due_date,
          hours_spent: parseFloat(formData.hoursSpent) || 0
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success(`${maintenanceType} maintenance logged successfully`);

      // Fetch updated device information
      try {
        const deviceResponse = await axios.get(
          API_ENDPOINTS.DEVICES.GET(device.id),
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        // Call onSubmit callback with updated device data
        if (onSubmit) {
          onSubmit({
            maintenanceLog: response.data,
            updatedDevice: deviceResponse.data
          });
        }
      } catch (fetchError) {
        console.error('Error fetching updated device:', fetchError);
        // Still call onSubmit even if device fetch fails
        if (onSubmit) {
          onSubmit({ maintenanceLog: response.data });
        }
      }

      // Close modal
      onClose();
    } catch (error) {
      console.error('Error logging maintenance:', error);
      toast.error(error.response?.data?.error || 'Failed to log maintenance work');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !device) return null;

  const modalContent = (
    <>
      {/* Barcode Scanner Modal */}
      {showScanner && (
        <BarcodeScanner
          onScan={handleDeviceScan}
          onClose={() => setShowScanner(false)}
          title="Verify Device"
          subtitle="Scan the device QR code to verify before logging maintenance."
          expectedDeviceId={device?.device_identifier}
        />
      )}

      {/* Main Modal */}
      <div className="form-modal-overlay maintenance-logging-overlay" onClick={handleRequestClose}>
        <div className="form-modal maintenance-logging-modal" onClick={(e) => e.stopPropagation()}>
          <div className="form-modal-header">
            <h3 className="form-modal-title">✓ Log Completed Maintenance</h3>
            <ModalCloseButton onClick={handleRequestClose} title="Close" />
          </div>

          <div className="form-modal-content">
            <form onSubmit={handleSubmit}>
              {!deviceScanned ? (
                // SCAN-FIRST MODE: Only show device info and scan section until device is verified
                <>
                  <div className="form-section device-info-section">
                    <h3 className="section-title">Device Information</h3>
                    <div className="device-info-display">
                      <div className="info-row">
                        <span className="info-label">Device ID:</span>
                        <span className="info-value">
                          <DeviceIDBadge deviceId={device.device_identifier || 'N/A'} size="small" />
                        </span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Device Name:</span>
                        <span className="info-value">{device.name || 'N/A'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Maintenance Type:</span>
                        <span className="info-value">
                          <span
                            className={`maintenance-type-badge modal-badge modal-${maintenanceType.toLowerCase()}`}
                            data-type={maintenanceType.toLowerCase()}
                          >
                            {maintenanceType === 'PPM' && '🔧 PPM (Preventive Maintenance)'}
                            {maintenanceType === 'Calibration' && '⚙️ Calibration'}
                            {maintenanceType === 'Compliance' && '📋 Regulatory Compliance'}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {isCheckingAssignment && (
                    <div className="form-section scan-section scan-required">
                      <p className="form-helper-text">Checking your assignment...</p>
                    </div>
                  )}

                  {!isCheckingAssignment && assignmentError && (
                    <div className="form-section scan-section scan-required">
                      <div className="scan-error">
                        <p className="error-title">🔒 {assignmentError.title}</p>
                        <p className="error-message">{assignmentError.message}</p>
                      </div>
                    </div>
                  )}

                  {!isCheckingAssignment && userAssigned && (
                    <div className="form-section scan-section scan-required">
                      <h3 className="section-title">Scan Device to Continue</h3>
                      <p className="form-helper-text">
                        Scan the device QR code to verify physical presence before logging maintenance.
                      </p>
                      {scanError && (
                        <div className="scan-error">
                          <p className="error-title">{scanError.title}</p>
                          <p className="error-message">{scanError.message}</p>
                        </div>
                      )}
                      <button
                        type="button"
                        className="action-btn action-primary btn-scan-device"
                        onClick={() => setShowScanner(true)}
                        disabled={isCheckingAssignment}
                      >
                        <span className="action-icon">📷</span>
                        <span className="action-text">Scan Device</span>
                      </button>
                    </div>
                  )}

                  <div className="form-actions">
                    <button
                      type="button"
                      onClick={handleRequestClose}
                      className="action-btn action-secondary"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                // FORM MODE: Show full form after device is scanned (no repeated device info)
                <>
                  <DeviceVerifiedBanner scannedDeviceId={scannedDeviceId} />

                  <div className="maintenance-reason-info">
                    <p className="form-warning-enhanced">
                      This will log completed {maintenanceType} work and update the next due date for
                      this device. Upload at least one matching report before submitting.
                    </p>
                    <span
                      className={`maintenance-type-chip maintenance-type-badge modal-badge modal-${maintenanceType.toLowerCase()}`}
                      data-type={maintenanceType.toLowerCase()}
                    >
                      {maintenanceType === 'PPM' && '🔧 PPM'}
                      {maintenanceType === 'Calibration' && '⚙️ Calibration'}
                      {maintenanceType === 'Compliance' && '📋 Compliance'}
                    </span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="mlm-description" className="required">
                      Description of Work Performed
                    </label>
                    <textarea
                      id="mlm-description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Describe what maintenance work was performed, any issues found, parts replaced, technicians involved, etc."
                      className="form-textarea"
                      rows="4"
                      required
                    />
                    <p className="form-helper-text">
                      Include names of other technicians if they assisted.
                    </p>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="mlm-completionDate" className="required">
                        Completion Date
                      </label>
                      <input
                        id="mlm-completionDate"
                        type="date"
                        name="completionDate"
                        value={formData.completionDate}
                        onChange={handleInputChange}
                        min={dateRestrictions.completionMin}
                        max={dateRestrictions.completionMax}
                        className="form-input"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="mlm-nextDueDate" className="required">
                        Next Due Date
                      </label>
                      <input
                        id="mlm-nextDueDate"
                        type="date"
                        name="nextDueDate"
                        value={formData.nextDueDate}
                        onChange={handleInputChange}
                        min={dateRestrictions.min}
                        max={dateRestrictions.max}
                        className="form-input"
                        required
                      />
                      <p className="form-helper-text">Auto-populated from device frequency</p>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="mlm-hoursSpent" className="required">
                      Hours Spent
                    </label>
                    <input
                      id="mlm-hoursSpent"
                      type="number"
                      name="hoursSpent"
                      value={formData.hoursSpent}
                      onChange={handleInputChange}
                      min="0.5"
                      step="0.5"
                      placeholder="e.g., 1.5"
                      className="form-input"
                      required
                    />
                    <p className="form-helper-text">How many hours did you spend on this maintenance?</p>
                  </div>

                  <div className="form-section maintenance-logging-documents">
                    <h3 className="section-title required">Reports for this session</h3>
                    <p className="form-helper-text">
                      Upload a file or take a live photo of the completed report.{' '}
                      {maintenanceReportHelperLine}
                    </p>
                    <p className="form-helper-text" style={{ color: '#8a4b00' }}>
                      Upload at least one matching report (required) before you can log this
                      maintenance. If you cancel without submitting, uploaded reports stay on the
                      device; delete any you added this session if you do not want to keep them.
                    </p>
                    <DocumentManager
                      deviceId={device.id}
                      token={token}
                      user={user}
                      instanceId="session"
                      documentOptions={DOCUMENT_OPTIONS_FOR_TECHNICIAN}
                      allowedDocumentTypes={technicianListDocumentTypes}
                      uploadDocumentOptions={maintenanceDocumentOptions}
                      permissions={{ canUpload: true, canPreview: true, canDelete: true }}
                      deleteOnlyOwn
                      restrictDeleteToNewSinceInitialLoad
                      showOnlySessionDocuments
                      enableCameraCapture
                      onPastBaselineDocumentCountChange={setPastBaselineDocumentCount}
                      workType={maintenanceType}
                      emptyStateText="No reports uploaded in this session yet."
                    />
                  </div>

                  <div className="form-section maintenance-history-documents">
                    <button
                      type="button"
                      className="device-history-docs-toggle"
                      onClick={() => setHistoryDocsExpanded((open) => !open)}
                    >
                      <span className="toggle-icon">{historyDocsExpanded ? '▼' : '▶'}</span>
                      <span>
                        Previous reports on this device
                        {' '}
                        ({historicalDocumentCount})
                      </span>
                    </button>
                    {/* Keep mounted so baseline is captured when the form opens (not after session uploads). */}
                    <div
                      className="device-history-docs-body"
                      hidden={!historyDocsExpanded}
                    >
                      <p className="form-helper-text device-history-docs-help">
                        Read-only reference. Prior maintenance, calibration, and compliance reports
                        already on this device. Upload for this log is in the section above.
                      </p>
                      <div className="device-history-docs-panel maintenance-logging-documents">
                        <DocumentManager
                          deviceId={device.id}
                          token={token}
                          user={user}
                          instanceId="history"
                          documentOptions={DOCUMENT_OPTIONS_FOR_TECHNICIAN}
                          allowedDocumentTypes={technicianListDocumentTypes}
                          permissions={{ canUpload: false, canPreview: true, canDelete: false }}
                          showOnlyBaselineDocuments
                          onDocumentCountChange={setHistoricalDocumentCount}
                          emptyStateText="No previous reports on this device."
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-actions form-actions-close">
                    <button
                      type="button"
                      onClick={handleRequestClose}
                      className="action-btn action-secondary"
                      disabled={loading}
                    >
                      <span className="action-icon">←</span>
                      <span>Cancel</span>
                    </button>
                    <button
                      type="submit"
                      className="action-btn action-approve"
                      disabled={loading || pastBaselineDocumentCount < 1}
                      title={
                        pastBaselineDocumentCount < 1
                          ? 'Upload at least one report for this maintenance'
                          : undefined
                      }
                    >
                      <span className="action-icon">✓</span>
                      <span>{loading ? 'Logging...' : 'Log Maintenance'}</span>
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showCancelConfirm}
        entityName={device?.device_identifier || 'this device'}
        entityType="maintenance logging"
        action="close"
        actionVerb="close without logging"
        customMessage={
          pastBaselineDocumentCount === 1
            ? '1 report uploaded in this session will remain on the device under Documents & Attachments. Delete it here first if you do not want to keep it.'
            : `${pastBaselineDocumentCount} reports uploaded in this session will remain on the device under Documents & Attachments. Delete them here first if you do not want to keep them.`
        }
        onConfirm={finalizeClose}
        onCancel={() => setShowCancelConfirm(false)}
      />
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default MaintenanceLoggingModal;
